import { pxToEm } from 'util/pxToEm';
import { Box, Typography } from '@mui/material';
import useFeature, { FeatureFlag } from 'hooks/useFeature';
import React, { ReactElement, useMemo } from 'react';
import { Select } from '../Select';
import { TextInput } from '../TextInput';
import { Switch } from '../Switch';


const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const INTERVAL_OPTIONS = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
];

export interface RecurrenceOptionsValue {
  frequency: 'WEEKLY';
  interval: number;
  daysOfWeek: number[];
  seriesEndDate: string;
}

export interface RecurrenceOptionsProps {
  isRecurring: boolean;
  onToggleRecurring: (isRecurring: boolean) => void;
  value?: RecurrenceOptionsValue;
  onChange: (value: RecurrenceOptionsValue) => void;
  disabled?: boolean;
}

export const RecurrenceOptions = ({
  isRecurring,
  onToggleRecurring,
  value,
  onChange,
  disabled = false,
}: RecurrenceOptionsProps): ReactElement | null => {
  const isRecurringEventsEnabled = useFeature(FeatureFlag.RecurringEvents);

  const handleIntervalChange = (interval: number) => {
    if (!value) return;
    onChange({
      ...value,
      interval,
    });
  };

  const handleDayToggle = (day: number) => {
    if (!value) return;
    const currentDays = value.daysOfWeek || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day].sort((a, b) => a - b);

    if (newDays.length === 0) return;

    onChange({
      ...value,
      daysOfWeek: newDays,
    });
  };

  const handleEndDateChange = (seriesEndDate: string) => {
    if (!value) return;
    onChange({
      ...value,
      seriesEndDate,
    });
  };

  const MAX_OCCURRENCES = 25;

  const occurrenceCount = useMemo(() => {
    if (!value || !isRecurring || !value.seriesEndDate || value.daysOfWeek.length === 0) return 0;

    const startDate = new Date();
    const endDate = new Date(`${value.seriesEndDate}T23:59:59`);

    if (endDate <= startDate) return 0;

    let count = 0;
    const currentDate = new Date(startDate);

    while (currentDate <= endDate && count <= MAX_OCCURRENCES + 1) {
      const dayOfWeek = currentDate.getDay();

      if (value.daysOfWeek.includes(dayOfWeek)) {
        const weeksDiff = Math.floor(
          (currentDate.getTime() - startDate.getTime()) / (7 * 24 * 60 * 60 * 1000),
        );
        if (weeksDiff % value.interval === 0 || weeksDiff === 0) count++;
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return count;
  }, [value, isRecurring]);

  const exceedsMaxOccurrences = occurrenceCount > MAX_OCCURRENCES;

  const summaryText = useMemo(() => {
    if (!value || !isRecurring) return '';

    const selectedDays = value.daysOfWeek
      .map(d => DAYS_OF_WEEK.find(day => day.value === d)?.label)
      .filter(Boolean);

    if (selectedDays.length === 0 || !value.seriesEndDate) return '';

    const dayNames: Record<string, string> = {
      Sun: 'Sunday',
      Mon: 'Monday',
      Tue: 'Tuesday',
      Wed: 'Wednesday',
      Thu: 'Thursday',
      Fri: 'Friday',
      Sat: 'Saturday',
    };

    const fullDayNames = selectedDays.map(d => {
      const dayName = d ? dayNames[d] : undefined;
      return dayName || d || '';
    });

    let daysText: string;
    if (fullDayNames.length === 1) {
      daysText = fullDayNames[0] || '';
    } else {
      const lastDay = fullDayNames[fullDayNames.length - 1] || '';
      daysText = `${fullDayNames.slice(0, -1).join(', ')} and ${lastDay}`;
    }

    const endDate = new Date(`${value.seriesEndDate}T00:00:00`);
    const formattedDate = endDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    const intervalText = value.interval === 1 ? 'week' : `${String(value.interval)} weeks`;

    return `Repeats every ${intervalText} on ${daysText} until ${formattedDate}`;
  }, [value, isRecurring]);

  if (!isRecurringEventsEnabled) return null;

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        gap={12}
      >
        <Typography
          sx={{
            fontSize: pxToEm(18),
            fontWeight: 600,
            color: '#23305F',
          }}
        >
          Recurring Event
        </Typography>
        <Switch
          on={isRecurring}
          onToggle={() => onToggleRecurring(!isRecurring)}
          disabled={disabled}
        />
        <Typography
          sx={{
            fontSize: pxToEm(14),
            fontWeight: 400,
            color: '#23305F',
          }}
        >
          {isRecurring ? 'Yes' : 'No'}
        </Typography>
      </Box>

      {isRecurring && value && (
        <Box sx={{ mt: 24 }}>
          <Box
            sx={theme => ({
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              mb: 24,
              flexWrap: 'wrap',
              [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
                alignItems: 'flex-start',
              },
            })}
          >
            <Typography
              sx={{
                fontSize: pxToEm(14),
                fontWeight: 500,
                color: '#23305F',
                minWidth: 90,
              }}
            >
              Repeat every
            </Typography>
            <Box sx={{ width: 80 }}>
              <Select
                value={value.interval}
                onChange={e => handleIntervalChange(Number(e.target.value))}
                disabled={disabled}
              >
                {INTERVAL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </Box>
            <Typography
              sx={{
                fontSize: pxToEm(14),
                fontWeight: 400,
                color: '#23305F',
              }}
            >
              Week(s)
            </Typography>
          </Box>

          <Box sx={{ mb: 24 }}>
            <Typography
              sx={{
                fontSize: pxToEm(14),
                fontWeight: 500,
                color: '#23305F',
                mb: 12,
              }}
            >
              Repeat on
            </Typography>
            <Box
              sx={theme => ({
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
                [theme.breakpoints.down('sm')]: {
                  gap: 6,
                },
              })}
            >
              {DAYS_OF_WEEK.map(day => (
                <Box
                  key={day.value}
                  onClick={() => !disabled && handleDayToggle(day.value)}
                  sx={theme => ({
                    minWidth: 48,
                    height: 40,
                    px: 12,
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: disabled ? 'default' : 'pointer',
                    backgroundColor: value.daysOfWeek.includes(day.value)
                      ? '#23305F'
                      : 'transparent',
                    color: value.daysOfWeek.includes(day.value)
                      ? 'white'
                      : '#23305F',
                    transition: 'all 0.2s',
                    userSelect: 'none',
                    '&:hover': disabled
                      ? {}
                      : {
                          backgroundColor: value.daysOfWeek.includes(day.value)
                            ? '#1a2548'
                            : '#F5F5F5',
                        },
                    [theme.breakpoints.down('sm')]: {
                      minWidth: 40,
                      px: 8,
                    },
                  })}
                >
                  <Typography
                    sx={{
                      fontSize: pxToEm(14),
                      fontWeight: 500,
                    }}
                  >
                    {day.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ mb: 16 }}>
            <Typography
              sx={{
                fontSize: pxToEm(14),
                fontWeight: 500,
                color: '#23305F',
                mb: 8,
              }}
            >
              Ends on
            </Typography>
            <Box sx={{ maxWidth: 200 }}>
              <TextInput
                type="date"
                value={value.seriesEndDate}
                onChange={e => handleEndDateChange(e.target.value)}
                disabled={disabled}
                style={{
                  backgroundColor: '#F5F5F5',
                }}
              />
            </Box>
          </Box>

          {exceedsMaxOccurrences ? (
            <Typography
              sx={{
                fontSize: pxToEm(14),
                fontWeight: 300,
                color: '#D32F2F',
                mt: 16,
              }}
            >
              Number of occurrences must not exceed 25.
            </Typography>
          ) : summaryText ? (
            <Typography
              sx={{
                fontSize: pxToEm(14),
                fontWeight: 400,
                color: '#666666',
                mt: 16,
              }}
            >
              {summaryText}
            </Typography>
          ) : null}
        </Box>
      )}
    </Box>
  );
};
